import { PayloadAction, createSelector, createSlice } from '@reduxjs/toolkit';
import { RootState } from '..';
import {
  CommonFilter,
  CommonFilterKeyPayload,
  CommonFilterModel,
  CommonFilterObject,
  CommonFilterPayload,
  CommonFilterPayloadAction,
  CommonFiltersPayload,
  CommonFiltersPayloadAction,
  DatasetID,
} from '../../models/filter.model';

// identifier
export const COMMON_FILTER_SLICE_ID = 'commonFilters';

// default state
const initialState: CommonFilterModel = {};

//slice
const commonFilterSlice = createSlice({
  name: COMMON_FILTER_SLICE_ID,
  initialState,
  reducers: {
    removeCommonFilter: (
      state: CommonFilterModel,
      action: PayloadAction<CommonFilterKeyPayload>
    ) => {
      const { datasetId, filterKey } = action.payload;

      const filterSet = state[datasetId];
      if (filterSet) delete filterSet.filters[filterKey];
      state[datasetId] = filterSet;
    },
    updateCommonFilter: (state: CommonFilterModel, action: PayloadAction<CommonFilterPayload>) => {
      const { datasetId, filter } = action.payload;

      let filterSet = state[datasetId];
      if (!filterSet) {
        filterSet = { filters: {}, appliedFilters: {} };
      }
      if (!filterSet.filters[filter.key]) {
        filterSet.filters[filter.key] = {
          type: filter.type,
          label: filter?.label,
          value: [],
        };
      }

      filterSet.filters[filter.key] = filter.filter;

      state[datasetId] = filterSet;
    },
    updateCommonFilters: (
      state: CommonFilterModel,
      action: PayloadAction<CommonFiltersPayload>
    ) => {
      const { datasetId, filters } = action.payload;
      const filterSet: CommonFilter = { filters: {}, appliedFilters: {} };

      (filters as CommonFilterPayloadAction[]).forEach((filter) => {
        if (!filterSet.appliedFilters[filter.key]) {
          filterSet.appliedFilters[filter.key] = {
            type: filter.type,
            label: filter?.label,
            value: [],
          };
        }
        filterSet.appliedFilters[filter.key].value = filter.filter.value;
      });
      filterSet.filters = filterSet.appliedFilters;
      state[datasetId] = filterSet;
    },

    addCommonFilters: (state: CommonFilterModel, action: PayloadAction<CommonFiltersPayload>) => {
      const { datasetId, filters } = action.payload;

      const appliedFilters = state[datasetId]?.appliedFilters || {};
      const filterSet = state[datasetId]?.filters || {};

      (filters as CommonFiltersPayloadAction[]).forEach((filter) => {
        if (filterSet[filter.key]) {
          filterSet[filter.key].value = filter.filters.reduce(
            (acc: CommonFilterObject[], current: CommonFilterObject) => {
              if (acc.find((item) => item.value === current.value)) {
                return acc;
              }
              return [...acc, current];
            },
            filterSet[filter.key].value
          );
        } else {
          filterSet[filter.key] = {
            type: filter.type,
            label: filter?.label,
            value: filter.filters,
          };
        }
      });

      state[datasetId] = { appliedFilters, filters: filterSet };
    },

    removeCommonFilters: (
      state: CommonFilterModel,
      action: PayloadAction<CommonFiltersPayload>
    ) => {
      const { datasetId, filters } = action.payload;

      const appliedFilters = state[datasetId]?.appliedFilters || {};
      const filterSet = state[datasetId]?.filters || {};

      (filters as CommonFiltersPayloadAction[]).forEach((filter) => {
        // Check if the filter key exists in filterSet
        if (filterSet[filter.key]?.value) {
          const updatedFilters = filterSet[filter.key].value.filter((item: CommonFilterObject) => {
            // Remove the filters where item.value matches one of the filterItem values
            const result = !filter.filters.find((filterItem) => filterItem.value === item.value);
            return result;
          });

          if (updatedFilters.length > 0) {
            filterSet[filter.key].value = updatedFilters;
          }

          if (updatedFilters.length === 0) {
            delete filterSet[filter.key];
          }
        }
      });

      state[datasetId] = { appliedFilters, filters: filterSet };
    },
    applyCommonFilter: (state: CommonFilterModel, action: PayloadAction<DatasetID>) => {
      const { datasetId } = action.payload;
      const filterSet = state[datasetId];
      if (filterSet) {
        filterSet.appliedFilters = filterSet.filters;

        state[datasetId] = filterSet;
      } else {
        state[datasetId] = { filters: {}, appliedFilters: {} };
      }
    },
    removeAppliedFilter: (
      state: CommonFilterModel,
      action: PayloadAction<CommonFilterKeyPayload>
    ) => {
      const { datasetId, filterKey } = action.payload;
      const filterSet = state[datasetId];
      if (filterSet) delete filterSet.appliedFilters[filterKey];
      state[datasetId] = filterSet;
    },
    rollbackFilterChanges: (state: CommonFilterModel, action: PayloadAction<DatasetID>) => {
      const { datasetId } = action.payload;
      const filterSet = state[datasetId];
      if (filterSet) {
        filterSet.filters = filterSet.appliedFilters;
        state[datasetId] = filterSet;
      } else {
        state[datasetId] = { filters: {}, appliedFilters: {} };
      }
    },
    clearFilters: (state: CommonFilterModel, action: PayloadAction<Partial<DatasetID>>) => {
      if (action.payload.datasetId) {
        const { datasetId } = action.payload;
        const filterSet = state[datasetId];
        if (filterSet) {
          filterSet.filters = {};
          filterSet.appliedFilters = {};
        }
        state[datasetId] = filterSet;
      } else {
        Object.keys(state).forEach((key: string) => {
          state[key] = null;
        });
      }
    },
  },
});

//selectors
export const selectCommonFilters = createSelector(
  [(state: RootState) => state.commonFilters, (state: RootState, datasetId: string) => datasetId],
  (commonFilters, datasetId) => {
    const filterSet = commonFilters[datasetId];
    if (filterSet) {
      return filterSet.filters;
    }
    return {};
  }
);

export const selectSurveyFilters = createSelector(
  [(state: RootState) => state.commonFilters, (state: RootState, datasetId: string) => datasetId],
  (commonFilters, datasetId) => {
    const surveyValues = commonFilters[datasetId]?.filters['Survey']?.value ?? [];
    const questionValues = commonFilters[datasetId]?.filters['Question']?.value ?? [];
    const responseValues = commonFilters[datasetId]?.filters['ResponseOption']?.value ?? [];

    return [...surveyValues, ...questionValues, ...responseValues] as {
      name: string;
      value: string;
    }[];
  }
);

export const selectAppliedFilters = createSelector(
  [(state: RootState) => state.commonFilters, (_: RootState, datasetId: string) => datasetId],
  (commonFilters, datasetId) => {
    const filterSet = commonFilters[datasetId];
    if (filterSet) {
      return filterSet.appliedFilters;
    }
    return {};
  }
);

//actions
export const {
  updateCommonFilter,
  updateCommonFilters,
  removeCommonFilter,
  addCommonFilters,
  removeCommonFilters,
  applyCommonFilter,
  clearFilters,
  rollbackFilterChanges,
  removeAppliedFilter,
} = commonFilterSlice.actions;

//reducer
export default commonFilterSlice;
